import React, {ReactNode, useEffect, useState} from 'react';
import './BookingOverlay.css'
import {ChooseTime} from "./ChooseTime";
import {ConfirmBooking} from "./ConfirmBooking";
import {
  GetProviderProfileRequest,
  GetProviderProfileResponse,
  IApiBusinessProfileProto,
  IBookAppointmentResponse,
  space
} from "./compiled";
import {BookingComplete} from "./BookingComplete";
import {SendUnauthenticatedRpc} from "../util";
import {RpcState} from "../util/RpcState";
import {ChooseService} from "./ChooseService";
import {ChooseLocation} from "./ChooseLocation";
import {ChoosePractitioner} from "./ChoosePractitioner";
import {useProfile} from "../util/UserProfileProvider";
import {useAuth0} from "@auth0/auth0-react";
import {CreateKensuAccount} from "./CreateKensuAccount";
import {BookingOverlayProps} from "../BookingOverlayApp";
import {Payment} from "./Payment";
import IProviderBioProto = space.kenko.proto.IProviderBioProto;
import IServiceProto = space.kenko.proto.IServiceProto;
import IBusinessLocationProto = space.kenko.proto.IBusinessLocationProto;
import IPriceProto = space.kenko.proto.IPriceProto;
import icon_arrow_back from './icon_arrow_back.png';
import icon_close from './icon_close.png';

/**
 * 1. Select service (skip if only one or already specified)
 * 2. Choose location (skip if only one or already specified) -- WEIRD IF VIRTUAL. So this comes AFTER location.
 * 3. Select practitioner (or any)
 * 4. Show times (for 1, or all if not selected).
 *
 * There's an initial fetch to figure out which locations, services,
 * and practitioners are available at the office.
 *
 * Once the location, service, and (optionally) practitioner are selected,
 * then the times are fetched.and
 *
 * @param props
 * @constructor
 */
export const BookingOverlay: React.FC<BookingOverlayProps> = (props) => {

  const {user} = useAuth0();
  const {profile, profileLoading} = useProfile();

  const [location, setLocation] = useState<IBusinessLocationProto>();
  const [service, setService] = useState<IServiceProto>();
  const [practitioner, setPractitioner] = useState<IProviderBioProto>();
  const [timestamp, setTimestamp] = useState<number>();

  const [rpcState, setRpcState] = useState<RpcState>({state: 'none'})
  const [business, setBusiness] = useState<IApiBusinessProfileProto>();

  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState<string>();

  const [bookingConfirmation, setBookingConfirmation] = useState<IBookAppointmentResponse>();

  useEffect(() => {

    console.log('Booking Overlay props', props)

    setRpcState({state: 'pending'});

    SendUnauthenticatedRpc('get_business_details',
        GetProviderProfileRequest.encode(new GetProviderProfileRequest({
          businessId: props.businessId,
          version: props.version,
        })).finish())
        .then(responseBytes => {
          let response = GetProviderProfileResponse.decode(responseBytes);
          if (!response.okay || !response.business) {
            setRpcState({state: 'error', message: 'Error loading this provider'});
            return;
          }
          setRpcState({state: 'success'});
          setBusiness(response.business);
        })
        .catch(e => {
          setRpcState({state: 'error', message: e.toString()});
        })
  }, [])

  useEffect(() => {

    if (!business) {
      setService(undefined);
      setLocation(undefined);
      setPractitioner(undefined);
      setTimestamp(undefined);
      return;
    }

    // This chooses the defaults (if any!) based on how the user came into this overlay.
    // They might have selected a generic "Book now!" or "Book this service" or even
    // "Book with Joe". So we need to set those as the default.
    if (props.serviceSku && business.practice?.services) {
      for (let i = 0; i < business.practice.services.length; i++) {
        if (business.practice.services[i].sku == props.serviceSku) {
          setService(business.practice.services[i]);
        }
      }
    }

    if (props.providerId && business.providers) {
      for (let i = 0; i < business.providers.length; i++) {
        if (business.providers[i].providerId == props.providerId) {
          setPractitioner(business.providers[i]);
        }
      }
    }

  }, [business]);

  // Price is available only if both service and provider are selected. 
  const getPrice = (): IPriceProto | undefined | null => {
    if (!practitioner || !service || !service.offeredBy) {
      return null
    }

    for (let i = 0; i < service.offeredBy.length; i++) {
      const offeredBy = service.offeredBy[i];
      if (offeredBy.providerId == practitioner.providerId) {
        return offeredBy.price;
      }
    }

    return null;
  }

  // This is opposite order of selection. Note that anything passed in
  // the parameters in NOT changeable -- so if you click "book with Bo", 
  // Bo will always remain as the provider.
  const onBackButton = () => {

    if (timestamp) {
      setTimestamp(undefined);
      return;
    }

    if (practitioner && !props.providerId) {
      setPractitioner(undefined);
      return;
    }

    if (location && !props.locationId) {
      setLocation(undefined);
      return;
    }

    if (service && !props.serviceSku) {
      setService(undefined);
      return;
    }

    props.onCancel && props.onCancel();
  }


  /**
   *
   * @param things if title is unspecified, uses 'Book Appointment'
   */
  const getDefaultPageView = (things: { title?: string, bottom: ReactNode }) => {
    return <div className={'BookingOverlayModal'}>
      <div className={'BookingOverlay'}>

        <div className={'BookingOverlayTopRow BookingOverlaySectionMargin'}>
          <button className={'BookingOverlayClose'} onClick={onBackButton}>
            <img className={'BookingOverlayIcon'} alt={'Back'} src={icon_arrow_back}/>
          </button>

          <div className={'BookingOverlayHeader'}>{things.title || 'Book Appointment'}</div>

          <button className={'BookingOverlayClose'} onClick={props.onCancel}>
            <img className={'BookingOverlayIcon'} alt={'Close'} src={icon_close}/>
          </button>
        </div>
        {things.bottom}
      </div>
    </div>
  }

  const getTabbedBottomSection = (things: { leftSide: ReactNode, content: ReactNode }) => {
    return <div className={'BookingOverlayBottomRow'}>
      {things.leftSide}
      <div className={'BookingOverlayBottomRight BookingOverlaySectionMargin'}>
        {things.content}
      </div>
    </div>
  }


  const getLeftSide = () => {

    if (!practitioner && !service && !location && !timestamp) {

      // Nothing has been selected! In this case, don't show the left nav at all.
      // Once the service is selected, then show the left nav.
      return <></>
    }

    // Special case when there's ONLY a practitioner selected -- you get a special bio on 
    // the left side. This is to show credentials in a multi-practitioner office.
    if (practitioner && !service && !location && !timestamp) {
      return <div className={'BookingOverlayBottomLeft BookingOverlaySectionMargin'}>
        <div className={'SpecialProviderBio'}>
          <div className={'SpecialProviderBioTitleRow'}>
            <img className={'SpecialProviderBioPhoto'} src={practitioner.imageUrls ? practitioner.imageUrls[0] : ''}/>
            <div>
              <div><b>{practitioner.firstName} {practitioner.lastName}</b></div>
              <div>{practitioner.jobTitle}</div>
            </div>
          </div>
          <p>{practitioner.bio}</p>

          <p style={{marginBlockEnd: 0, textDecoration: 'underline'}}>Education</p>
          <ul style={{marginBlockStart: 0}}>
            {practitioner.education?.map(e => {
              return <li>{e.school}, {e.degree}</li>
            })}
          </ul>

          <p style={{marginBlockEnd: 0, textDecoration: 'underline'}}>Credentials</p>
          <ul style={{marginBlockStart: 0}}>
            {practitioner.credentials?.map(credential => {
              return <li>{credential.licenseOrCredential}</li>
            })}
          </ul>
        </div>

        <button className={'BookingChangeLink'} onClick={() => {
          setPractitioner(undefined);
          setTimestamp(undefined);
        }}>Change practitioner
        </button>
      </div>
    }


    return <div className={'BookingOverlayBottomLeft BookingOverlaySectionMargin'}>
      {service && <div>
        <div><b>{service.name} ({service.lengthMinutes} min)
          {getPrice() && ` · $${getPrice()?.price}.00`}</b></div>
        <button className={'BookingChangeLink'} onClick={() => {
          setService(undefined);
          setTimestamp(undefined);
        }}>Change service
        </button>
      </div>}

      {location && <div>
        <div><b>{location.locationName}</b></div>
        <div>{location.address}</div>
        <button className={'BookingChangeLink'} onClick={() => {
          setLocation(undefined);
          setTimestamp(undefined);
        }}>Change location
        </button>
      </div>}

      {practitioner && <div>
        <div><b>{practitioner.firstName} {practitioner.lastName}</b></div>
        <div>{practitioner.jobTitle}</div>
        <button className={'BookingChangeLink'} onClick={() => {
          setPractitioner(undefined);
          setTimestamp(undefined);
        }}>Change practitioner
        </button>
      </div>}

      {timestamp && <div>
        <div><b>{new Date(timestamp).toLocaleString(undefined, {
          dateStyle: 'long', timeStyle: 'short'
        })}</b></div>
        <button className={'BookingChangeLink'} onClick={() => setTimestamp(undefined)}>Change date/time</button>
      </div>}
    </div>
  }

  if (rpcState.state == 'pending') {
    return getDefaultPageView({
      bottom: <div className={'BookingOverlayBottomRow BookingOverlaySectionMargin'}>LOADING</div>
    });
  }

  if (rpcState.state == "error") {
    return getDefaultPageView({
      bottom: <div className={'BookingOverlayBottomRow BookingOverlaySectionMargin'}>ERROR: {rpcState.message}</div>
    });
  }

  // * 2. Select service (skip if only one or already specified) -- Choosing "virtual" location is awkward!
  // * 1. Choose location (skip if only one or already specified)
  // * 3. Select practitioner (or any)
  // * 4. Show times (for 1, or all if not selected).

  if (!service) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <ChooseService business={business}
                                practitionerFilter={practitioner?.providerId}
                                onSelect={setService}/>
      })
    });
  }


  if (!location) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <ChooseLocation business={business} onSelect={setLocation}/>
      })
    });
  }

  if (!practitioner) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <ChoosePractitioner locationFilter={location}
                                     serviceFilter={service}
                                     business={business}
                                     onSelect={setPractitioner}/>
      })
    });
  }


  if (!timestamp) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <ChooseTime businessId={props.businessId}
                             serviceSku={service.sku || ''}
                             providerId={practitioner.providerId as string}
                             onCancel={props.onCancel}
                             onSelected={setTimestamp}/>
      })
    });
  }

  // Here lies the login gate. If there's no profile / user it must be created
  // for sure definite. 
  if (!user) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <CreateKensuAccount/>
      })
    });
  }

  if (profileLoading) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <div style={{padding: 20}}>Loading profile...</div>
      })
    });
  }

  if (!profile) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <div style={{padding: 20}}>
          We're sorry, it looks like you are not
          in the Kensu alpha! Booking is currently limited to
          alpha participants.</div>
      })
    });
  }

  // NEED TO LOCK EVERYTHING AFTER THIS!! You shouldn't be able to change the
  // practitioner mid-payment.


  // Handles the payment authorization via a stripe embedded widget. This only runs an
  // authorization, NOT a payment. The payment is done after the class is booked.
  // 
  // If the card is declined, an error shows up right in this payment widget so
  // we do not have to handle that case as far as I can tell.
  //
  // In the future this step may be skipped if the user already has a class credit.
  if (!paymentComplete) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <Payment
            businessId={business?.businessId as string}
            serviceSku={service.sku as string}
            providerId={practitioner.providerId as string}
            locationId={location.locationId as string}
            businessStripeAccountId={business?.stripeAccountId as string}
            onComplete={(sessionId) => {
              console.log('Payment complete');
              setPaymentComplete(true)
              setPaymentSessionId(sessionId);
            }}/>
      })
    })
  }

  // This page redeems the now-acquired item. 
  if (!bookingConfirmation) {
    return getDefaultPageView({
      bottom: getTabbedBottomSection({
        leftSide: getLeftSide(),
        content: <ConfirmBooking
            businessId={props.businessId}
            sessionId={paymentSessionId as string}
            provider={practitioner}
            service={service}
            onComplete={response => {
              if (response.updatedProfile?.appointments && props.onAppointmentsUpdated) {
                props.onAppointmentsUpdated(response.updatedProfile.appointments);
              }
              setBookingConfirmation(response)
            }}
            appointmentTime={timestamp}
            isVirtual={false} // todo....
            locationId={location.locationId ?? ''}
            onCancel={() => props.onCancel && props.onCancel()}
        />
      })
    })
  }

  return getDefaultPageView({
    title: 'Appointment Confirmed 🎉',
    bottom: <div className={'BookingOverlayBottomRow BookingOverlaySectionMargin'}>
      <BookingComplete response={bookingConfirmation}
                       onDone={() => props.onCancel && props.onCancel()}/>
    </div>
  });
}